import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { IncomeStructureByKvedGeneralInfoSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/IncomeStructureByKvedGeneralInfoSection.tsx';
import { IncomeStructureByKvedIncomesSection } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/IncomeStructureByKvedIncomesSection.tsx';
import {
  applyIncomeStructureByKvedGeneralInfo,
  deserializeIncomeStructureByKvedCertificate,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/mapDtoToFormValues.ts';
import type { TIncomeStructureByKvedFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/types.ts';
import { getIncomeStructureByKvedDefaultValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/incomeStructureByKved/utils.ts';
import type { TDocumentGenerationFormProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/types.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';
import { HistoricalPrefillProvider } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/HistoricalPrefillProvider.tsx';
import { collectHistoricalPrefilledFieldNames } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/historicalPrefill/utils.ts';
import { useDocumentGenerationFormSubmission } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/hooks/useDocumentGenerationFormSubmission.ts';

export const IncomeStructureByKvedDocumentGenerationForm: React.FC<
  TDocumentGenerationFormProps
> = ({
  disabled,
  formId,
  onSaveIncomeStructureByKved,
  onLoadIncomeStructureByKved,
  onDirtyChange,
  historicalBody,
  isHistoricalSource,
  onHistoricalPrefillPendingFieldsChange,
  onSaveAttemptChange,
  onValidationIssuesChange,
}) => {
  const hasLoadedCertificateRef = useRef(false);
  const certificateVersionRef = useRef<number | null>(null);
  const [isLoadingCertificate, setIsLoadingCertificate] = useState(
    Boolean(onLoadIncomeStructureByKved)
  );
  const [historicalPrefilledFields, setHistoricalPrefilledFields] = useState<string[]>([]);
  const form = useForm<TIncomeStructureByKvedFormValues>({
    defaultValues: getIncomeStructureByKvedDefaultValues(),
  });
  const { reset } = form;
  const isFormDisabled = disabled || isLoadingCertificate;

  useEffect(() => {
    if (!onLoadIncomeStructureByKved || hasLoadedCertificateRef.current) {
      return;
    }

    hasLoadedCertificateRef.current = true;

    void onLoadIncomeStructureByKved().then((response) => {
      if (!response) {
        setIsLoadingCertificate(false);
        return;
      }

      certificateVersionRef.current = response.version;

      const certificateBody =
        historicalBody && typeof historicalBody === 'object' ? historicalBody : response.body;
      const values = certificateBody
        ? deserializeIncomeStructureByKvedCertificate(
            certificateBody as NonNullable<typeof response.body>
          )
        : getIncomeStructureByKvedDefaultValues();

      const nextValues = applyIncomeStructureByKvedGeneralInfo(values, response);
      reset(nextValues);
      setHistoricalPrefilledFields(
        isHistoricalSource
          ? collectHistoricalPrefilledFieldNames(nextValues, [
              'borrowerFullName',
              'borrowerIdentifier',
              'soleProprietor',
              'reportYear',
            ])
          : []
      );
      setIsLoadingCertificate(false);
    });
  }, [historicalBody, isHistoricalSource, onLoadIncomeStructureByKved, reset]);

  const handleSubmit = async (values: TIncomeStructureByKvedFormValues) => {
    if (!onSaveIncomeStructureByKved) {
      return;
    }

    const version = certificateVersionRef.current;

    if (version === null) {
      return;
    }

    const result = await onSaveIncomeStructureByKved(values, version);

    if (result === 'success') {
      reset(values);

      void onLoadIncomeStructureByKved?.().then((response) => {
        if (response) {
          certificateVersionRef.current = response.version;
        }
      });
    }
  };

  const { attemptSave } = useDocumentGenerationFormSubmission({
    form,
    onDirtyChange,
    onSaveAttemptChange,
    onValidationIssuesChange,
    onValidSubmit: handleSubmit,
  });

  return (
    <FormProvider {...form}>
      <HistoricalPrefillProvider
        fieldNames={historicalPrefilledFields}
        onPendingFieldsChange={onHistoricalPrefillPendingFieldsChange}
      >
        <Box
          component="form"
          id={formId}
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            attemptSave();
          }}
          sx={documentGenerationFormStyles.form}
        >
          <IncomeStructureByKvedGeneralInfoSection disabled={isFormDisabled} />
          <IncomeStructureByKvedIncomesSection disabled={isFormDisabled} />
        </Box>
      </HistoricalPrefillProvider>
    </FormProvider>
  );
};
